import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { getDriverLicence } from "../../api/apiGetrequest";
import { UpdateDriverLincense } from "../../api/apirequest";
import TableHeader from "../../Components/DataTable/TableHeader";
import Base64ImageModal from "../../Components/ImageViewer/ImageViewer";
import LicenseForm from "../DriverApp/AddDriverDL";

// Styles
const styles = {
  container: {
    padding: "20px",
    fontFamily: "Arial, sans-serif",
  },
  searchInput: {
    width: "100%",
    padding: "10px",
    fontSize: "16px",
    marginBottom: "20px",
    borderRadius: "4px",
    border: "1px solid #ddd",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
    borderRadius: "8px",
    overflow: "hidden",
  },
  tableHeader: {
    backgroundColor: "#f5f5f5",
    fontWeight: "bold",
    padding: "12px",
    textAlign: "left",
  },
  tableRow: {
    borderBottom: "1px solid #ddd",
  },
  tableCell: {
    padding: "12px",
  },
  card: {
    backgroundColor: "#fff",
    borderRadius: "8px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
    padding: "16px",
    marginBottom: "16px",
  },
  cardTitle: {
    fontSize: "18px",
    fontWeight: "bold",
    marginBottom: "8px",
  },
  cardText: {
    fontSize: "14px",
    color: "#555",
    marginBottom: "8px",
  },
  button: {
    padding: "8px 16px",
    borderRadius: "4px",
    border: "none",
    cursor: "pointer",
    marginRight: "8px",
    fontSize: "14px",
  },
  viewButton: {
    backgroundColor: "#007bff",
    color: "#fff",
  },
  approveButton: {
    backgroundColor: "#28a745",
    color: "#fff",
  },
  statusPending: {
    color: "#ffa500", // Orange for Pending Approval
    fontWeight: "bold",
  },
  statusExpired: {
    color: "#dc3545", // Red for Expired
    fontWeight: "bold",
  },
  statusDue: {
    color: "#ffc107", // Yellow for Due Soon
    fontWeight: "bold",
  },
  statusActive: {
    color: "#28a745", // Green for Active
    fontWeight: "bold",
  },
};

const LicenseView = () => {
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= 768);
  const [data, setData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [license, setLicense] = useState(null);
  const [openmd, setOpenmd] = useState(null);
  const [loading, setLoading] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editRow, setEditRow] = useState(null);

  const fetchData = async () => {
    try {
      const { result, isFetching } = await getDriverLicence(0);
      setData(result);
      setLoading(isFetching);
    } catch (error) {
      console.error("Error fetching vehicles:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Handle screen size changes
  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Handle search input changes
  useEffect(() => {
    const filtered = data.filter((license) =>
      license.DriverName.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredData(filtered);
  }, [searchQuery, data]);

  // Determine the license status based on StatusID and NextRenewalDate
  const getLicenseStatus = (license) => {
    const currentDate = new Date();
    const nextRenewalDate = new Date(license.NextRenewalDate);
    const daysDifference = Math.ceil(
      (nextRenewalDate - currentDate) / (1000 * 60 * 60 * 24)
    );

    if (license.StatusID === 2) {
      return { status: "Pending Approval", style: styles.statusPending };
    } else if (nextRenewalDate < currentDate) {
      return { status: "Expired", style: styles.statusExpired };
    } else if (daysDifference <= 7) {
      return {
        status: `Due in ${daysDifference} Days`,
        style: styles.statusDue,
      };
    } else {
      return { status: "Active", style: styles.statusActive };
    }
  };

  const handleViewImage = (row) => {
    setLicense(row);
    setOpenmd(true);
  };

  const handleUpdateDL = (row) => {
    setEditRow(row);
    setShowForm(true);
  };

  const handleApproveLicense = async (row) => {
    setLoading(true);

    try {
      const updateData = {
        licenseID: row.LicenseID,
        nextRenewalDate: row.NextRenewalDate,
        licenseImage: row.LicenseImage,
        dateUpdated: row.DateUpdated,
        statusID: 1,
      };

      const result = await UpdateDriverLincense(updateData);

      if (result && result.success) {
        toast.success(result.message || "License successfully approved!");
      } else {
        toast.error(
          result.message || "An error occurred while approving the license."
        );
      }

      fetchData();
    } catch (error) {
      console.error(error);
      toast.error("There was an error processing your request.");
    } finally {
      setLoading(false);
    }
  };

  if (showForm) {
    return (
      <>
        <TableHeader
          title="Vehicle List"
          showAddButton={false}
          showBackButton={true}
          onBack={() => {
            setEditRow(null);
            setShowForm(null);
          }}
          showPrintButton={true}
          showExcelButton={true}
          showPdfButton={true}
        />
        <LicenseForm
          licence={editRow}
          fetchData={fetchData}
          onClose={() => setShowForm(null)}
        />
      </>
    );
  }

  return (
    <div style={styles.container}>
      <TableHeader
        title="Vehicle List"
        showAddButton={true}
        showBackButton={false}
        onAdd={() => {
          setEditRow(null);
          setShowForm(true);
        }}
        showPrintButton={true}
        showExcelButton={true}
        showPdfButton={true}
      />
      {/* Search Input */}
      <input
        type="text"
        placeholder="Search by driver name..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        style={styles.searchInput}
      />

      {isSmallScreen ? (
        // Card View for Small Screens
        <div>
          {filteredData.map((license) => {
            const { status, style } = getLicenseStatus(license);
            return (
              <div key={license.LicenseID} style={styles.card}>
                <div style={styles.cardTitle}>{license.DriverName}</div>
                <div style={styles.cardText}>
                  License No: {license.LicenseNO}
                </div>
                <div style={{ ...styles.cardText, ...style }}>
                  Status: {status}
                </div>
                <div style={styles.cardText}>
                  Next Renewal: {license.NextRenewalDate.split("T")[0]}
                </div>
                <div style={styles.cardText}>
                  Date Updated: {license.DateUpdated.split("T")[0]}
                </div>
                <button
                  style={{ ...styles.button, ...styles.viewButton }}
                  onClick={() => handleViewImage(license)}
                >
                  View Imagerrt
                </button>
                {license.StatusID === 2 && (
                  <button
                    dissabled={loading}
                    style={{ ...styles.button, ...styles.approveButton }}
                    onClick={() => handleApproveLicense(license)}
                  >
                    Approve
                  </button>
                )}{" "}
                <button
                  dissabled={loading}
                  style={{ ...styles.button, ...styles.approveButton }}
                  onClick={() => handleUpdateDL(license)}
                >
                  Update
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        // Table View for Large Screens
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.tableHeader}>Driver Name</th>
              <th style={styles.tableHeader}>License No</th>
              <th style={styles.tableHeader}>Status</th>
              <th style={styles.tableHeader}>Next Renewal</th>
              <th style={styles.tableHeader}>Date Updated</th>
              <th style={styles.tableHeader}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.map((license) => {
              const { status, style } = getLicenseStatus(license);
              return (
                <tr key={license.LicenseID} style={styles.tableRow}>
                  <td style={styles.tableCell}>{license.DriverName}</td>
                  <td style={styles.tableCell}>{license.LicenseNO}</td>
                  <td style={{ ...styles.tableCell, ...style }}>{status}</td>
                  <td style={styles.tableCell}>
                    {license.NextRenewalDate.split("T")[0]}
                  </td>
                  <td style={styles.tableCell}>
                    {license.DateUpdated.split("T")[0]}
                  </td>
                  <td style={styles.tableCell}>
                    <button
                      style={{ ...styles.button, ...styles.viewButton }}
                      onClick={() => handleViewImage(license)}
                    >
                      View Image
                    </button>
                    {license.StatusID === 2 && (
                      <button
                        style={{ ...styles.button, ...styles.approveButton }}
                        onClick={() => handleApproveLicense(license)}
                      >
                        Approve
                      </button>
                    )}
                    <button
                      dissabled={loading}
                      style={{ ...styles.button, ...styles.approveButton }}
                      onClick={() => handleUpdateDL(license)}
                    >
                      Update
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {license && (
        <Base64ImageModal
          base64Image={license?.LicenseImage}
          name={license?.FullName}
          open={openmd}
          onClose={() => setOpenmd(!openmd)}
        />
      )}
    </div>
  );
};

export default LicenseView;
